import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { z } from 'zod';

import {
  Alert,
  Button,
  Card,
  PageHeader,
  PageLayout,
  setSubmitError,
  useAppForm,
} from '@keboola/design';
import { CircleCheck } from '@keboola/design/icons';

import { useCurrentVendor } from '../../../auth/context';
import { COMPONENT_TYPE_LABELS } from '../../../features/apps/constants';
import { appIdSchema, createWebalizedAppId } from '../../../features/apps/create';
import {
  COMPONENT_TYPE_OTHER,
  COMPONENT_TYPES,
  type ComponentTypeOrOther,
} from '../../../features/apps/vocabulary';
import { getApiErrorMessage } from '../../../features/auth/errors';
import { formValidators } from '../../../features/auth/validate';
import { useCreateApp } from '../../../features/vendors/queries';

export const Route = createFileRoute('/~/components/new')({
  component: VendorComponentsNewPage,
  head: () => ({ meta: [{ title: 'Register a component | Keboola Developer Portal' }] }),
});

const SELECTABLE_TYPES: readonly ComponentTypeOrOther[] = [
  ...COMPONENT_TYPES,
  COMPONENT_TYPE_OTHER,
];

const TYPE_OPTIONS = SELECTABLE_TYPES.map((value) => ({
  value,
  label: COMPONENT_TYPE_LABELS[value],
}));

/** `appIdSchema` is the rule apps-api enforces at creation: 3–64 chars, lowercase alphanumerics with
 * single dashes, no trailing dash. */
const createFormSchema = z.object({
  name: z.string().min(1, 'Required').max(128),
  type: z.enum([...COMPONENT_TYPES, COMPONENT_TYPE_OTHER], { message: 'Pick a type' }),
  // `min(1)` first so a blank id reads "Required" instead of zod's default "Invalid input"
  // for the client schema's 3-char minimum.
  id: z.string().min(1, 'Required').pipe(appIdSchema),
});

/**
 * `type` starts as `''` because the select has no valid neutral member — the schema rejects it with
 * "Pick a type". Declared as a union rather than asserted, so nothing pretends an empty string is a
 * component type.
 */
type CreateFormValues = { name: string; type: ComponentTypeOrOther | ''; id: string };

const DEFAULT_VALUES: CreateFormValues = { name: '', type: '', id: '' };

function VendorComponentsNewPage() {
  const vendorId = useCurrentVendor();
  const createApp = useCreateApp(vendorId);
  const [createdId, setCreatedId] = useState<string | null>(null);

  const form = useAppForm({
    defaultValues: DEFAULT_VALUES,
    validators: formValidators(createFormSchema),
    canSubmitWhenInvalid: true,
    onSubmit: async ({ value, formApi }) => {
      // The `onSubmit` validator has already accepted these values; parsing again is what turns
      // `type: ComponentTypeOrOther | ''` into the payload type, which no validator can do.
      const parsed = createFormSchema.parse(value);

      try {
        // The 201 body is the created `App`, so its `id` is the canonical one apps-api assigned —
        // prefixed with the vendor, and whatever else it normalised. Composing `<vendorId>.<id>` here
        // would be a second implementation of a rule only the server owns.
        const created = await createApp.mutateAsync(parsed);
        setCreatedId(created.id);
      } catch (error) {
        setSubmitError(
          formApi,
          // apps-api answers a duplicate with a bare "Already exists", which tells the user nothing about
          // *what* already exists.
          getApiErrorMessage(error, 'Error while creating the component.') === 'Already exists'
            ? 'A component with this ID already exists.'
            : getApiErrorMessage(error, 'Error while creating the component.'),
        );
      }
    },
  });

  if (createdId !== null) {
    return (
      <PageLayout>
        <Card className="tw:mx-auto tw:max-w-2xl">
          <Card.Content className="tw:flex tw:flex-col tw:items-center tw:gap-4 tw:text-center">
            <div className="tw:text-success">
              <CircleCheck />
            </div>
            <Card.Title as="h1">Component added</Card.Title>
            <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
              Your component is registered but not working yet — it is private and needs more detail
              before it can run. A Keboola administrator reviews it before it can become public.
            </p>
            <div className="tw:flex tw:flex-wrap tw:justify-center tw:gap-3">
              <Button asChild variant="secondary">
                <Link to="/~">Go to components</Link>
              </Button>
              <Button asChild variant="primary">
                <Link to="/~/components/$appId/edit" params={{ appId: createdId }}>
                  Continue editing this component
                </Link>
              </Button>
            </div>
          </Card.Content>
        </Card>
      </PageLayout>
    );
  }

  return (
    // `narrow`, matching the edit routes this hands off to — a two-field form has no use for the
    // full window width.
    <PageLayout maxWidth="narrow">
      <PageLayout.Header>
        <PageHeader.Index
          title="Register a component"
          description="Name it and pick its type. Everything else comes next."
        />
      </PageLayout.Header>

      <Card className="tw:mx-auto tw:max-w-2xl">
        <Card.Content>
          <form.AppForm>
            <form.FormBody>
              {/* The id is auto-filled from name + type as the user types, exactly as the original
                  does, and stays editable afterwards. Wired through field `listeners` rather than an
                  `onValueChange` prop: the connectors deliberately omit that — the field owns its own
                  change handling — so a callback passed there is dropped silently. */}
              <form.AppField
                name="name"
                listeners={{
                  onChange: ({ value }) => {
                    form.setFieldValue(
                      'id',
                      createWebalizedAppId(value, form.getFieldValue('type')),
                    );
                  },
                }}
              >
                {/* No `autoFocus`, unlike the original: it moves focus away from the top of the page on
                    load, stranding screen-reader and keyboard users past the heading and breadcrumb.
                    `jsx-a11y/no-autofocus` enforces this. */}
                {(field) => (
                  <field.TextField
                    label="Name"
                    required
                    placeholder="Name without the type suffix, e.g. MySQL"
                  />
                )}
              </form.AppField>

              <form.AppField
                name="type"
                listeners={{
                  onChange: ({ value }) => {
                    form.setFieldValue(
                      'id',
                      createWebalizedAppId(form.getFieldValue('name'), value),
                    );
                  },
                }}
              >
                {(field) => (
                  <field.SelectField
                    label="Type"
                    required
                    items={TYPE_OPTIONS}
                    placeholder="Select a type"
                  />
                )}
              </form.AppField>

              <form.AppField name="id">
                {(field) => (
                  <field.TextField
                    label="ID"
                    required
                    placeholder="e.g. ex-mysql"
                    // The vendor prefix is display-only: apps-api adds it server-side, and sending an
                    // already-prefixed id would produce `acme.acme.ex-mysql`.
                    prefix={`${vendorId}.`}
                    help="Cannot be changed later."
                  />
                )}
              </form.AppField>

              <Alert variant="info">
                Some settings are chosen for you based on the type — the container registry, and the
                configuration UI the platform renders. You can change them while editing.
              </Alert>

              <form.SubmitErrorAlert />
              <form.SubmitButton>Add component</form.SubmitButton>
            </form.FormBody>
          </form.AppForm>
        </Card.Content>
      </Card>
    </PageLayout>
  );
}
